import { isRecord } from 'lib/object/objectUtils'
import {
  readTranslatedString,
  readTrimmedString,
  readTrimmedStringArray,
} from 'loyalty/lib/labPayloadUtils'

export interface LoyaltySpendPointsBoostTile {
  imageId: string
  title: string
  description: string
  ctaLabel: string
  ctaUrl: string
  startAt: string
  endAt: string
}

export interface LoyaltySpendPointsViewModel {
  waysToBoostTiles: Array<LoyaltySpendPointsBoostTile>
  hasResolvedRemoteConfig: boolean
}

const EMPTY_VIEW_MODEL: LoyaltySpendPointsViewModel = {
  waysToBoostTiles: [],
  hasResolvedRemoteConfig: false,
}

function isActiveForRegion(source: Record<string, unknown>, regionCode: string, now: Date): boolean {
  if (source.isActive !== true) return false

  const eligibleRegions = readTrimmedStringArray(source.eligibleRegions)
  if (eligibleRegions && eligibleRegions.length > 0 && !eligibleRegions.includes(regionCode)) return false

  const startAt = readTrimmedString(source.startAt)
  const endAt = readTrimmedString(source.endAt)
  if (!startAt || !endAt) return false

  const startTime = new Date(startAt).getTime()
  const endTime = new Date(endAt).getTime()
  const nowTime = now.getTime()
  return !Number.isNaN(startTime) && !Number.isNaN(endTime) && startTime <= nowTime && nowTime <= endTime
}

function mapBoostTile(value: unknown, regionCode: string, now: Date): LoyaltySpendPointsBoostTile | undefined {
  if (!isRecord(value) || !isActiveForRegion(value, regionCode, now)) return undefined

  const imageId = readTrimmedString(value.imageId)
  const title = readTranslatedString(value, 'title')
  const description = readTranslatedString(value, 'description')
  const ctaLabel = readTranslatedString(value, 'ctaLabel')
  const ctaUrl = readTrimmedString(value.ctaUrl)
  const startAt = readTrimmedString(value.startAt)
  const endAt = readTrimmedString(value.endAt)
  if (!imageId || !title || !description || !ctaLabel || !ctaUrl || !startAt || !endAt) return undefined

  return { imageId, title, description, ctaLabel, ctaUrl, startAt, endAt }
}

export function resolveLoyaltySpendPointsRemoteConfig(
  remoteConfig: App.LabRemoteConfigResult | undefined,
  regionCode: string,
  now = new Date(),
): LoyaltySpendPointsViewModel {
  const { value } = remoteConfig ?? {}
  if (!isRecord(value)) {
    return { ...EMPTY_VIEW_MODEL, hasResolvedRemoteConfig: !!remoteConfig }
  }

  const rawTiles = Array.isArray(value.waysToBoostTiles) ? value.waysToBoostTiles : []
  const waysToBoostTiles = rawTiles.reduce<Array<LoyaltySpendPointsBoostTile>>((tiles, value) => {
    const tile = mapBoostTile(value, regionCode, now)
    if (tile) tiles.push(tile)
    return tiles
  }, [])

  return { waysToBoostTiles, hasResolvedRemoteConfig: true }
}
